/* eslint-disable no-unused-vars */
import React, { useState } from 'react';

import {
  GoogleAnalyticsConstants,
  GoogleAnalyticsDisplayedEvent,
  GoogleAnalyticsEventContextWrapper,
} from '@vvoyages/google-analytics';
import Image from 'next/image';
import numWords from 'num-words';

import Button from '@/components/Button';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';

import styles from './ClaimBookingStep.module.scss';
import ClaimBookingRadioGroup from './controls/ClaimBookingRadioGroup';

type Sailor = {
  clientId: string;
  [key: string]: any;
};

interface ClaimBookingSailorSelectProps {
  tabId: string;
  data: {
    bookingNumber: string;
    sailors: Sailor[];
    noOfGuests?: number;
  };
  onSubmit: Function;
  onCancel: () => void;
}

const ClaimBookingSailorSelect: React.FC<ClaimBookingSailorSelectProps> = ({ tabId, data, onSubmit, onCancel }) => {
  const appUiData = useAppUiData();
  const [selectedSailor, setSelectedSailor] = useState(data.sailors?.[0]);
  const noOfGuests = data?.noOfGuests ?? 0;
  const sailorsCount = data?.sailors?.length ?? 0;
  const hasUnnamedSailor = noOfGuests - sailorsCount > 0;

  const handleChange = (value: Sailor) => {
    setSelectedSailor(value);
  };

  const handleSubmit = async () => {
    const value = {
      bookingNumber: data.bookingNumber,
      user: {
        clientID: selectedSailor.clientId,
        lastName: selectedSailor.name?.last,
        birthday: selectedSailor.dob,
      },
    };
    onSubmit(value);
  };

  return (
    <WizardStep tabId={tabId}>
      <GoogleAnalyticsEventContextWrapper category="VV-account-sign-in|we-found-your-booking">
        <GoogleAnalyticsDisplayedEvent eventAction={GoogleAnalyticsConstants.EVENT_ACTION.DISPLAYED} />
        <h2 className={styles.title}>{appUiData[appUiIds.claimBooking.foundBookingTitle]}</h2>
        <p className={styles.subtitle}>
          {appUiData[appUiIds.claimBooking.foundBookingSubtitle].replace('{N}', numWords(data.sailors.length))}
        </p>
        <form className={styles.selectForm}>
          <div className={styles.radioGroup}>
            <ClaimBookingRadioGroup
              sailors={data.sailors}
              selectedSailor={selectedSailor}
              onChange={handleChange}
              labelFunction={(sailor, index) =>
                `${sailor.name?.first} ${sailor.name?.last} ${(index + 1).toString().padStart(2, '0')}`
              }
            />
            {hasUnnamedSailor && (
              <div className={styles.unnamedSailorAlert}>
                <span className={styles.unnamedSailorHeader}>
                  <p className={styles.unnamedSailorText}>Unnamed Sailor</p>
                  <Image src="/auth/v1/static/image/disabledRadio.svg" width={20} height={20} alt="" />
                </span>
                <p className={styles.unnamedSailorInfo}>
                  (If you are unnamed please contact your primary sailor or booking agent to be added to the booking
                  before continuing)
                </p>
                <hr className={styles.radioDivider} />
              </div>
            )}
          </div>
          <Button variant="primary" onClick={handleSubmit} disabled={false} className={styles.buttonSearch}>
            {appUiData[appUiIds.commonLabels.search]}
          </Button>
          <Button onClick={onCancel} className={styles.cancelLink}>
            {appUiData[appUiIds.commonLabels.cancel]}
          </Button>
        </form>
      </GoogleAnalyticsEventContextWrapper>
    </WizardStep>
  );
};

export default ClaimBookingSailorSelect;
